<template>
  <div
    class="ca-quick-action-card flex flex-col rounded-size-5"
    :class="[
      containerLayout,
      backgroundClass,
      {
        'ca-quick-action-card--clickable': url && !isLoading,
        'ca-quick-action-card--loading': isLoading,
        'ca-quick-action-card--on-green': isBackgroundGreen,
      },
    ]"
    @click="handleCardClick"
  >
    <!-- Loading: shimmer placeholders com a variante de fundo do card -->
    <template v-if="isLoading">
      <div
        class="ca-quick-action-card__header flex items-center justify-between gap-size-3"
      >
        <div
          class="ca-quick-action-card__shimmer ca-quick-action-card__shimmer--avatar"
        ></div>
      </div>

      <div
        class="ca-quick-action-card__content flex items-end justify-between gap-size-3"
      >
        <div class="ca-quick-action-card__text flex flex-col gap-size-2 flex-1">
          <div
            class="ca-quick-action-card__shimmer ca-quick-action-card__shimmer--title"
          ></div>
          <div
            class="ca-quick-action-card__shimmer ca-quick-action-card__shimmer--description"
          ></div>
        </div>
      </div>
    </template>

    <!-- Header: avatar (esquerda) + chevron alinhado ao topo (direita) -->
    <div
      v-if="hasIcon && !isLoading"
      class="ca-quick-action-card__header flex items-center justify-between gap-size-3"
    >
      <RoundedAvatarV2
        :backgroundColorClass="avatarBgClass"
        :image="image"
        :size="avatarSize"
      />
      <a
        v-if="url"
        :href="url"
        :target="target || undefined"
        :aria-label="title"
        class="ca-quick-action-card__chevron-link flex-shrink-0"
      >
        <ChevronRight class="ca-quick-action-card__chevron" />
      </a>
      <ChevronRight
        v-else
        class="ca-quick-action-card__chevron flex-shrink-0"
      />
    </div>

    <div
      v-if="!isLoading"
      class="ca-quick-action-card__content flex flex-col gap-size-3"
      :class="{ 'ca-quick-action-card__content--no-icon': !hasIcon }"
    >
      <component
        :is="normalizeHeadingTag(titleHeadingType, 'p')"
        class="ca-quick-action-card__title"
        :class="titleTypography"
      >
        {{ title }}
      </component>
      <div
        v-if="description || !hasIcon"
        class="ca-quick-action-card__meta flex items-center justify-between gap-size-3"
      >
        <component
          v-if="description"
          :is="normalizeHeadingTag(descriptionHeadingType, 'p')"
          class="ca-quick-action-card__description caption-medium"
        >
          {{ description }}
        </component>
        <!-- Sem ícone: chevron alinhado com a description -->
        <a
          v-if="!hasIcon && url"
          :href="url"
          :target="target || undefined"
          :aria-label="title"
          class="ca-quick-action-card__chevron-link flex-shrink-0"
        >
          <ChevronRight class="ca-quick-action-card__chevron" />
        </a>
        <ChevronRight
          v-else-if="!hasIcon"
          class="ca-quick-action-card__chevron flex-shrink-0"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { RoundedAvatarV2 } from "ca-online-design-system";
import ChevronRight from "@assets/chevron-green-right.svg";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaQuickActionCard",
});

const props = defineProps({
  /** Image URL shown inside the DS RoundedAvatar. */
  image: {
    type: String,
    default: "",
  },
  /** Avatar background variant: 'verde', 'solarized' or 'branco'. */
  avatarBackgroundColorClass: {
    type: String,
    default: "",
  },
  /** URL opened when the chevron is clicked. */
  url: {
    type: String,
    default: "",
  },
  /** Anchor target for the chevron link (e.g. '_blank'). */
  target: {
    type: String,
    default: "",
  },
  /** Card title. */
  title: {
    type: String,
    default: "",
  },
  titleHeadingType: {
    type: String,
    default: "Paragraph",
  },
  /** Card description shown below the title. */
  description: {
    type: String,
    default: "",
  },
  descriptionHeadingType: {
    type: String,
    default: "Paragraph",
  },
  isBackgroundGreen: {
    type: Boolean,
    default: false,
  },
  /** Card background variant: 'Laranja', 'Verde' or 'Branco'. */
  backgroundColor: {
    type: String,
    default: "branco",
    validator: (v) => ["laranja", "verde", "branco"].includes(v),
  },
});

const containerLayout = computed(() =>
  isDesktop.value ? "p-size-5 gap-size-4" : "p-size-4 gap-size-4",
);

const hasIcon = computed(() => !!props.image);

/**
 * Estado de loading derivado internamente: enquanto o conteúdo essencial
 * (o título) ainda não chegou, o card mostra o shimmer. Assim que os dados
 * estiverem disponíveis, renderiza o conteúdo normal sem placeholders.
 */
const isLoading = computed(() => !props.title);

/** Navigate to the card URL, mirroring the chevron link behaviour. */
function handleCardClick(event) {
  if (!props.url || isLoading.value) return;
  // Let the actual chevron link handle its own clicks.
  if (event.target.closest(".ca-quick-action-card__chevron-link")) return;

  if (props.target === "_blank") {
    window.open(props.url, "_blank", "noopener");
  } else {
    window.location.href = props.url;
  }
}

/**
 * Tamanho do avatar consoante o viewport: mobile/tablet usam 44px e desktop
 * usa 56px.
 */
const avatarSize = computed(() => (isDesktop.value ? "56px" : "44px"));

const backgroundClass = computed(
  () =>
    ({
      laranja: "ca-quick-action-card--laranja",
      verde: "ca-quick-action-card--verde",
      branco: "ca-quick-action-card--branco",
    })[props.backgroundColor],
);

/**
 * Map the avatar background variant ('verde', 'solarized' or 'branco') to the
 * DS colour token. The RoundedAvatar prefixes it with `bg-` internally.
 */
const avatarBgClass = computed(
  () =>
    ({
      verde: "backgrounds-container-high",
      solarized: "solarized",
      branco: "backgrounds-surface",
    })[props.avatarBackgroundColorClass] ?? props.avatarBackgroundColorClass,
);

// Deteta desktop (>= 1024px) para escolher a tipografia do título:
// mobile/tablet usam "paragraph-medium" e desktop usa "heading-small".
const isDesktop = ref(false);

const updateScreen = () => {
  isDesktop.value = window.innerWidth >= 1024;
};

onMounted(() => {
  updateScreen();
  window.addEventListener("resize", updateScreen);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScreen);
});

const titleTypography = computed(() => {
  if (!hasIcon.value) return "paragraph-bold";
  return isDesktop.value ? "heading-small" : "paragraph-bold";
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Base (mobile first) — layout uses DS utility classes in the template
// (flex, flex-col, p-size-*, gap-size-*, rounded-size-4).
// ---------------------------------------------------------------------------
.ca-quick-action-card {
  color: var(--ca-color-text-body);

  &--clickable {
    cursor: pointer;
  }

  &:has(:focus-visible) {
    // Outline desenhado para dentro do cartão (offset negativo) para não ser
    // cortado pelo `overflow` do scroll container que o envolve (o carrossel).
    // Assim o foco aparece nos 4 lados, mesmo no primeiro/último cartão.
    outline: 1px solid var(--ca-color-feedback-info);
    outline-offset: -2px;
  }
}

// ---------------------------------------------------------------------------
// Background variants
// ---------------------------------------------------------------------------
.ca-quick-action-card--laranja {
  background:
    linear-gradient(
      0deg,
      var(--ca-color-feedback-warning-light, rgba(233, 108, 0, 0.2)) 0%,
      var(--ca-color-feedback-warning-light, rgba(233, 108, 0, 0.2)) 100%
    ),
    var(--ca-color-neutrals-inverted, #fff);

  &:hover {
    background:
      linear-gradient(
        0deg,
        var(--ca-color-on-background-inverted-30, rgba(255, 255, 255, 0.3)) 0%,
        var(--ca-color-on-background-inverted-30, rgba(255, 255, 255, 0.3)) 100%
      ),
      linear-gradient(
        0deg,
        var(--ca-color-feedback-warning-light, rgba(233, 108, 0, 0.2)) 0%,
        var(--ca-color-feedback-warning-light, rgba(233, 108, 0, 0.2)) 100%
      ),
      var(--ca-color-backgrounds-surface, #fff);
  }

  &:active {
    background:
      linear-gradient(
        0deg,
        var(--ca-color-on-background-inverted-60, rgba(255, 255, 255, 0.6)) 0%,
        var(--ca-color-on-background-inverted-60, rgba(255, 255, 255, 0.6)) 100%
      ),
      linear-gradient(
        0deg,
        var(--ca-color-feedback-warning-light, rgba(233, 108, 0, 0.2)) 0%,
        var(--ca-color-feedback-warning-light, rgba(233, 108, 0, 0.2)) 100%
      ),
      var(--ca-color-backgrounds-surface, #fff);
  }
}

.ca-quick-action-card--branco {
  background: var(--ca-color-neutrals-inverted, #fff);

  &:hover,
  &:focus-within {
    background: var(--ca-color-hover-pressed-neutral-hover);
  }

  &:active {
    background: var(--ca-color-hover-pressed-neutral-pressed);
  }

  // Card branco sobre fundo verde: usa os tokens de hover/pressed próprios.
  &.ca-quick-action-card--on-green {
    &:hover,
    &:focus-within {
      background: var(--ca-color-hover-pressed-container-high-hover);
    }

    &:active {
      background: var(--ca-color-hover-pressed-inverted-hover-light);
    }
  }
}

.ca-quick-action-card--verde {
  background: var(--ca-color-backgrounds-container-high, #e5f4ef);

  &:hover {
    background:
      linear-gradient(
        0deg,
        var(--ca-color-neutrals-inverted-30) 0%,
        var(--ca-color-neutrals-inverted-30) 100%
      ),
      var(--ca-color-backgrounds-container-high);
  }

  &:active {
    background:
      linear-gradient(
        0deg,
        var(--ca-color-neutrals-inverted-60) 0%,
        var(--ca-color-neutrals-inverted-60) 100%
      ),
      var(--ca-color-backgrounds-container-high);
  }
}

// ---------------------------------------------------------------------------
// Chevron
// ---------------------------------------------------------------------------
.ca-quick-action-card__chevron-link {
  display: inline-flex;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none;
  }
}

.ca-quick-action-card__chevron {
  display: inline-flex;

  :deep(svg) {
    display: block;
    width: var(--size-2);
    height: var(--size-4);
  }
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------
.ca-quick-action-card__title {
  margin: 0;
  color: var(--ca-color-text-headers);
  word-wrap: break-word;
}

.ca-quick-action-card__description {
  margin: 0;
  color: var(--ca-color-text-body-variant);
  word-break: normal;
  overflow-wrap: normal;
}

// Keep the chevron on the right even when there is no description.
.ca-quick-action-card__meta {
  > .ca-quick-action-card__chevron-link,
  > .ca-quick-action-card__chevron {
    margin-left: auto;
  }
}

// No avatar: fill the card height and pin the description + chevron to the bottom.
.ca-quick-action-card__content--no-icon {
  flex: 1;

  .ca-quick-action-card__meta {
    margin-top: auto;
  }
}

// ---------------------------------------------------------------------------
// Shimmer / loading
// Cada variante de cor tem a sua própria classe de shimmer (base + highlight),
// derivada da cor de fundo do card.
// ---------------------------------------------------------------------------
.ca-quick-action-card--loading {
  cursor: default;
  pointer-events: none;
}

.ca-quick-action-card__shimmer {
  border-radius: var(--size-2);
  background-size: 200% 100%;
  animation: ca-quick-action-card-shimmer 1.5s ease-in-out infinite;

  &--avatar {
    width: var(--size-9);
    height: var(--size-9);
    border-radius: var(--radius-full, 9999px);
    flex-shrink: 0;
  }

  &--title {
    height: var(--size-6);
    border-radius: var(--size-3);
    width: 100%;
  }

  &--description {
    height: 22px;
    width: 100%;
    border-radius: var(--size-3);
  }
}

// Variante branca — cinzas neutros sobre fundo branco.
.ca-quick-action-card--branco .ca-quick-action-card__shimmer {
  background-image: linear-gradient(
    90deg,
    var(--ca-color-empresas-solarized-medium, #edecea) 0%,
    var(--ca-color-backgrounds-container) 100%
  );
}

// Variante verde — tons derivados de --ca-color-backgrounds-container-high.
.ca-quick-action-card--verde .ca-quick-action-card__shimmer {
  background-image: linear-gradient(
    270deg,
    var(--ca-color-neutrals-inverted-10) 0%,
    var(--ca-color-theme-primary-20) 100%
  );
}

// Variante laranja — tons derivados de --ca-color-feedback-warning-light.
.ca-quick-action-card--laranja .ca-quick-action-card__shimmer {
  background-image: linear-gradient(
    90deg,
    var(--ca-color-theme-secondary-30) 0%,
    rgba(233, 108, 0, 0.1) 100%
  );
}

@keyframes ca-quick-action-card-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
